import { useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { EditableFieldWrapper } from '@/components/ui/EditableFieldWrapper';
import { InlineEditActions } from '@/components/ui/InlineEditActions';
import { useInlineEdit } from '@/hooks/useInlineEdit';

type InputType = 'text' | 'email' | 'tel' | 'textarea';

interface InlineEditFieldProps {
  /** Prospect ID for the save call */
  prospectId: string;
  /** Field name (for display in error messages) */
  fieldName: string;
  /** Current field value */
  currentValue: string;
  /** Input type */
  inputType?: InputType;
  /** Save handler — receives prospectId and a record with the field update */
  onSave: (prospectId: string, data: Record<string, string>) => Promise<void>;
  /** Field key sent to API (e.g., "name", "company_name") */
  fieldKey: string;
  /** Icon shown next to label */
  icon?: React.ReactNode;
  /** Label text */
  label: string;
  /** Placeholder text when empty in view mode */
  emptyText?: string;
  /** Whether this field is currently being edited (controlled by parent) */
  isEditing?: boolean;
  /** Called when this field wants to start editing */
  onEditStart?: () => void;
  /** Called when editing ends */
  onEditEnd?: () => void;
  /** Additional content shown in view mode (e.g., copy button) */
  viewModeExtra?: React.ReactNode;
}

export function InlineEditField({
  prospectId,
  fieldName,
  currentValue,
  inputType = 'text',
  onSave,
  fieldKey,
  icon,
  label,
  emptyText,
  isEditing: controlledIsEditing,
  onEditStart,
  onEditEnd,
  viewModeExtra,
}: InlineEditFieldProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const isMultiline = inputType === 'textarea';

  const {
    isEditing: internalIsEditing,
    editValue,
    setEditValue,
    startEditing,
    cancelEditing,
    handleSave,
    handleKeyDown,
    handleBlur,
  } = useInlineEdit({
    currentValue: currentValue || '',
    onSave: async (value: string) => {
      await onSave(prospectId, { [fieldKey]: value });
    },
    isMultiline,
    onEditStart,
    onEditEnd,
  });

  const isEditing = controlledIsEditing !== undefined ? controlledIsEditing : internalIsEditing;

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Place cursor at end
      if ('setSelectionRange' in inputRef.current) {
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (!isEditing) {
      startEditing();
    }
  };

  if (isEditing) {
    return (
      <div data-testid={`inline-edit-${fieldKey}`}>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
          {icon}
          {label}
        </div>
        {isMultiline ? (
          <Textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={emptyText || `Add ${fieldName}...`}
            className="min-h-[80px]"
            data-testid={`inline-edit-${fieldKey}-input`}
          />
        ) : (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={inputType}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={emptyText || `Add ${fieldName}...`}
            data-testid={`inline-edit-${fieldKey}-input`}
          />
        )}
        <InlineEditActions
          onCancel={cancelEditing}
          onSave={handleSave}
          showEnterHint={!isMultiline}
        />
      </div>
    );
  }

  return (
    <EditableFieldWrapper
      isEditing={false}
      onEdit={handleEdit}
      className="rounded-md p-2"
      tooltipText={`Click to edit ${fieldName}`}
    >
      <div data-testid={`inline-edit-${fieldKey}`}>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
          {icon}
          {label}
        </div>
        <div className="flex items-center gap-2">
          {currentValue ? (
            <span className="text-sm">{currentValue}</span>
          ) : (
            <span className="text-sm text-muted-foreground italic">
              {emptyText || `Add ${fieldName}...`}
            </span>
          )}
          {viewModeExtra}
        </div>
      </div>
    </EditableFieldWrapper>
  );
}
